import React, { useEffect, useMemo, useState, useRef } from 'react';
import api, { apiErrorMessage } from '@/lib/api';
import AuthImage from '@/components/AuthImage';
import { VoiceDictationButton } from '@/components/VoiceDictationButton';
import { toast } from 'sonner';
import {
    ChevronDown, ChevronRight, Layers, Sparkles, Camera, Loader2,
    CheckCircle2, XCircle, MinusCircle, AlertTriangle, Shield,
} from 'lucide-react';

const ACCENT = '#7C6BEF';
const AI_ORANGE = '#F59E0B';
const WEIGHT_COLOR = { 1: '#9CA3AF', 2: '#3B82F6', 3: '#EF4444' };
const WEIGHT_LABEL = { 1: 'Estándar', 2: 'NOM', 3: 'Crítico' };

const FLAG_STYLES = {
    ok: { bg: 'rgba(16,185,129,0.15)', color: '#10B981', label: 'IA OK' },
    warning: { bg: 'rgba(245,158,11,0.15)', color: AI_ORANGE, label: 'IA · Revisar' },
    critical: { bg: 'rgba(239,68,68,0.15)', color: '#EF4444', label: 'IA · Crítico' },
};

/**
 * Renderiza el checklist específico del rubro (con soporte a:
 *  - Todos los answer_type (yes_no, numeric, text, options, date, photo)
 *  - subform_type per_instance (multiplica el checklist por N instancias)
 *  - visibility_condition (mostrar sólo si otro ítem tiene cierto valor)
 *  - IA photo analysis (POST /inspections/{id}/analyze-photo)
 */
export default function RubroChecklistSection({
    inspectionId,
    industry,
    open,
    onToggle,
    responses,
    onAnswer,
    onObs,
    disabled = false,
}) {
    const [rubro, setRubro] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [photoAnalyses, setPhotoAnalyses] = useState({}); // { item_id_instance_idx: analysis }
    const [uploadingItem, setUploadingItem] = useState(null);

    useEffect(() => {
        if (!industry) return;
        api.get(`/rubros/by-name/${encodeURIComponent(industry)}`)
            .then((r) => setRubro(r.data))
            .catch(() => setNotFound(true));
        api.get(`/inspections/${inspectionId}/photo-analyses`)
            .then((r) => {
                const map = {};
                (r.data || []).forEach((p) => { map[p.item_id] = p; });
                setPhotoAnalyses(map);
            })
            .catch(() => {});
    }, [industry, inspectionId]);

    // Envolver onAnswer para detectar cambios en per_instance y limpiar respuestas huérfanas
    const wrappedAnswer = (itemId, value) => {
        onAnswer(itemId, value);
        if (!rubro) return;
        const item = (rubro.items || []).find((i) => i.id === itemId);
        if (!item || item.subform_type !== 'per_instance' || item.answer_type !== 'numeric') return;
        const oldN = parseInt(responses.answers[itemId] || 0, 10);
        const newN = parseInt(value || 0, 10);
        if (!(newN < oldN)) return;
        // Buscar los sub-items del mismo área con phase=evaluacion
        const subItems = (rubro.items || []).filter((x) =>
            x.id !== itemId && x.phase === 'evaluacion' && (x.area || '') === (item.area || '')
        );
        // Borrar respuestas / observaciones para instancias >= newN
        let cleaned = 0;
        for (let idx = newN; idx < oldN; idx++) {
            for (const sub of subItems) {
                const key = `${sub.id}__${idx}`;
                if (responses.answers[key] !== undefined) { onAnswer(key, undefined); cleaned++; }
                if (responses.observations?.[key] !== undefined) { onObs(key, ''); }
            }
        }
        if (cleaned > 0) {
            toast.info(`Se limpiaron ${cleaned} respuesta(s) de instancias eliminadas (${oldN} → ${newN})`);
        }
    };

    const evaluate = (val, op, target) => {
        if (val == null) return false;
        const a = String(val).toLowerCase();
        const b = String(target).toLowerCase();
        switch (op) {
            case 'equals': return a === b;
            case 'not_equals': return a !== b;
            case 'contains': return a.includes(b);
            case 'greater_than': return parseFloat(a) > parseFloat(b);
            case 'less_than': return parseFloat(a) < parseFloat(b);
            default: return true;
        }
    };
    const shouldShow = (item) => {
        const c = item.visibility_condition;
        if (!c || !c.when_item_id) return true;
        return evaluate(responses.answers[c.when_item_id], c.operator, c.value);
    };

    const uploadPhoto = async (item, key, file) => {
        if (!file) return;
        setUploadingItem(key);
        try {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('item_id', key);
            fd.append('question', item.question);
            fd.append('answer', responses.answers[key] || '');
            fd.append('observations', responses.observations[key] || '');
            fd.append('standard_reference', item.standard_reference || '');
            const r = await api.post(`/inspections/${inspectionId}/analyze-photo`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setPhotoAnalyses((prev) => ({ ...prev, [key]: r.data }));
            toast.success('Foto analizada por IA');
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al analizar foto'));
        } finally {
            setUploadingItem(null);
        }
    };

    if (notFound || !industry) {
        return null; // sección oculta si no hay rubro coincidente
    }

    if (!rubro) {
        return (
            <div className="mb-3 p-4" style={{ background: 'var(--card-bg)', border: `1px solid ${ACCENT}` }}>
                <div className="text-sm opacity-70">Cargando checklist del rubro…</div>
            </div>
        );
    }

    const items = rubro.items || [];
    if (items.length === 0) return null;

    // Agrupar por área
    const byArea = {};
    items.forEach((it) => {
        const a = it.area || 'General';
        (byArea[a] = byArea[a] || []).push(it);
    });

    // Contar respondidos (solo visibles)
    const visibleItems = items.filter(shouldShow);
    const answered = visibleItems.filter((it) => {
        // ítem per_instance con numérico: siempre lo damos por respondido si tiene valor
        if (it.subform_type === 'per_instance') return !!responses.answers[it.id];
        return !!responses.answers[it.id];
    }).length;

    return (
        <div className="mb-3" style={{ background: 'var(--card-bg)', border: `1px solid ${ACCENT}` }} data-testid="section-rubro">
            <button type="button" onClick={onToggle}
                className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-black/[0.02] dark:hover:bg-white/5">
                <div className="flex items-center gap-3 min-w-0">
                    {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
                    <Layers className="w-4 h-4 shrink-0" style={{ color: ACCENT }} />
                    <span className="font-display font-bold text-sm text-left">
                        Checklist del rubro
                        <span className="ml-2 text-xs opacity-70">— {rubro.name}</span>
                        {rubro.is_ai_generated && (
                            <span className="ml-2 text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5"
                                style={{ background: 'rgba(245,158,11,0.15)', color: AI_ORANGE }}>IA</span>
                        )}
                    </span>
                </div>
                <span className="text-xs text-gray-500 shrink-0">{answered}/{visibleItems.length}</span>
            </button>
            {open && (
                <div className="px-5 pb-5 pt-2 border-t space-y-6" style={{ borderColor: 'var(--border)' }}>
                    {Object.entries(byArea).map(([area, list]) => (
                        <div key={area}>
                            <div className="text-[10px] uppercase tracking-widest font-bold mb-3" style={{ color: ACCENT }}>
                                ▸ {area} · {list.length} ítems
                            </div>
                            <div className="space-y-3">
                                {list.map((it) => {
                                    if (!shouldShow(it)) return null;
                                    return (
                                        <RubroItemRow
                                            key={it.id}
                                            item={it}
                                            allItems={items}
                                            responses={responses}
                                            onAnswer={wrappedAnswer}
                                            onObs={onObs}
                                            uploadPhoto={uploadPhoto}
                                            uploadingItem={uploadingItem}
                                            photoAnalyses={photoAnalyses}
                                            disabled={disabled}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function RubroItemRow({ item, allItems, responses, onAnswer, onObs, uploadPhoto, uploadingItem, photoAnalyses, disabled = false }) {
    const val = responses.answers[item.id];
    const currentObs = responses.observations[item.id] || '';
    // Iter 92 · mismo fix de closure obsoleto que ChecklistItem: leer el valor
    // vigente al momento en que llega la transcripción, no al iniciar a grabar.
    const obsRef = useRef(currentObs);
    obsRef.current = currentObs;

    // Per-instance: si el ítem es numérico + per_instance, respuesta = número de instancias
    // Los ítems del mismo área con phase='evaluacion' se multiplican
    const isRelevamiento = item.phase === 'relevamiento' || item.subform_type === 'per_instance';
    const instances = item.subform_type === 'per_instance' ? parseInt(val || 0, 10) : 0;

    return (
        <div className="border p-3" style={{ borderColor: 'var(--border)' }} data-testid={`rubro-item-${item.id}`}>
            <div className="flex items-start gap-3 mb-2">
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1 mb-1">
                        {isRelevamiento && (
                            <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5"
                                style={{ background: 'rgba(168,85,247,0.15)', color: '#A855F7' }}>RELEVAMIENTO</span>
                        )}
                        {item.requires_photo && (
                            <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 inline-flex items-center gap-1"
                                style={{ background: 'rgba(245,158,11,0.15)', color: AI_ORANGE }}>
                                <Camera className="w-2.5 h-2.5" /> FOTO+IA
                            </span>
                        )}
                    </div>
                    <p className="text-sm font-medium">{item.question}</p>
                    {item.tooltip && <div className="text-[11px] opacity-70 mt-1">💡 {item.tooltip}</div>}
                    {item.standard_reference && (
                        <div className="text-[11px] opacity-60 font-mono mt-1">📜 {item.standard_reference}</div>
                    )}
                </div>
                <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 shrink-0"
                    style={{ background: `${WEIGHT_COLOR[item.weight]}22`, color: WEIGHT_COLOR[item.weight] }}>
                    {WEIGHT_LABEL[item.weight]}
                </span>
            </div>

            {/* Input de respuesta según tipo */}
            <AnswerInput item={item} value={val} onChange={(v) => onAnswer(item.id, v)} disabled={disabled} />

            {/* Sub-instancias (si per_instance con numeric) */}
            {item.subform_type === 'per_instance' && instances > 0 && item.answer_type === 'numeric' && (
                <PerInstanceExpander
                    parent={item}
                    count={instances}
                    allItems={allItems}
                    responses={responses}
                    onAnswer={onAnswer}
                    onObs={onObs}
                    uploadPhoto={uploadPhoto}
                    uploadingItem={uploadingItem}
                    photoAnalyses={photoAnalyses}
                    disabled={disabled}
                />
            )}

            {/* Observaciones */}
            <div className="flex items-center gap-2 mt-2">
                <textarea rows={1} value={currentObs}
                    onChange={(e) => onObs(item.id, e.target.value)}
                    placeholder="Observaciones (opcional)..."
                    data-testid={`obs-${item.id}`}
                    disabled={disabled}
                    className="flex-1 px-3 py-1.5 border outline-none text-xs bg-transparent resize-none disabled:opacity-50"
                    style={{ borderColor: 'var(--border)' }} />
                <VoiceDictationButton
                    disabled={disabled}
                    testId={`obs-mic-${item.id}`}
                    onResult={(text) => onObs(item.id, obsRef.current ? `${obsRef.current} ${text}` : text)}
                />
            </div>

            {/* Foto + análisis IA */}
            {(item.requires_photo || item.answer_type === 'photo') && (
                <PhotoAnalyzer
                    item={item}
                    instanceKey={item.id}
                    uploadPhoto={uploadPhoto}
                    uploading={uploadingItem === item.id}
                    analysis={photoAnalyses[item.id]}
                    disabled={disabled}
                />
            )}
        </div>
    );
}

function AnswerInput({ item, value, onChange, disabled = false }) {
    switch (item.answer_type) {
        case 'yes_no':
            return (
                <div className="flex gap-1.5" data-testid={`answer-${item.id}`}>
                    {[
                        { v: 'yes', label: 'Sí', color: '#10B981', Icon: CheckCircle2 },
                        { v: 'no', label: 'No', color: '#EF4444', Icon: XCircle },
                        { v: 'na', label: 'N/A', color: '#6B7280', Icon: MinusCircle },
                    ].map(({ v, label, color, Icon }) => (
                        <button key={v} onClick={() => onChange(v)}
                            data-testid={`ans-${item.id}-${v}`}
                            type="button" disabled={disabled}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 border text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{
                                borderColor: value === v ? color : 'var(--border)',
                                background: value === v ? `${color}22` : 'transparent',
                                color: value === v ? color : 'inherit',
                            }}>
                            <Icon className="w-3.5 h-3.5" />{label}
                        </button>
                    ))}
                </div>
            );
        case 'numeric':
            return (
                <input type="number" value={value || ''} onChange={(e) => onChange(e.target.value)}
                    placeholder={item.example_answer || 'Cantidad'}
                    data-testid={`ans-${item.id}`}
                    disabled={disabled}
                    className="w-full px-3 py-2 border outline-none text-sm bg-transparent disabled:opacity-50"
                    style={{ borderColor: 'var(--border)' }} />
            );
        case 'text':
            return (
                <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)}
                    placeholder={item.example_answer || 'Respuesta'}
                    data-testid={`ans-${item.id}`}
                    disabled={disabled}
                    className="w-full px-3 py-2 border outline-none text-sm bg-transparent disabled:opacity-50"
                    style={{ borderColor: 'var(--border)' }} />
            );
        case 'options':
            return (
                <select value={value || ''} onChange={(e) => onChange(e.target.value)}
                    data-testid={`ans-${item.id}`}
                    disabled={disabled}
                    className="w-full px-3 py-2 border outline-none text-sm bg-transparent disabled:opacity-50"
                    style={{ borderColor: 'var(--border)' }}>
                    <option value="">— Selecciona —</option>
                    {(item.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
            );
        case 'date':
            return (
                <input type="date" value={value || ''} onChange={(e) => onChange(e.target.value)}
                    data-testid={`ans-${item.id}`}
                    disabled={disabled}
                    className="w-full px-3 py-2 border outline-none text-sm bg-transparent disabled:opacity-50"
                    style={{ borderColor: 'var(--border)' }} />
            );
        case 'photo':
            return null; // handled by PhotoAnalyzer
        default:
            return null;
    }
}

function PhotoAnalyzer({ item, instanceKey, uploadPhoto, uploading, analysis, disabled = false }) {
    return (
        <div className="mt-2 border-l-2 pl-3 py-2" style={{ borderColor: AI_ORANGE }}>
            <div className="flex items-center gap-2 mb-2">
                <Camera className="w-3.5 h-3.5" style={{ color: AI_ORANGE }} />
                <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: AI_ORANGE }}>
                    Foto de evidencia · Analizada por IA
                </span>
            </div>
            {analysis?.photo_url ? (
                <div>
                    <div className="flex items-start gap-3">
                        <AuthImage src={analysis.photo_url}
                            className="w-24 h-24 object-cover border" style={{ borderColor: 'var(--border)' }} />
                        <div className="flex-1 min-w-0 space-y-1">
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5"
                                style={{
                                    background: FLAG_STYLES[analysis.ai_flag]?.bg,
                                    color: FLAG_STYLES[analysis.ai_flag]?.color,
                                }}>
                                {analysis.ai_flag === 'critical' && <AlertTriangle className="w-3 h-3" />}
                                {analysis.ai_flag === 'ok' && <Shield className="w-3 h-3" />}
                                {FLAG_STYLES[analysis.ai_flag]?.label || 'IA'}
                            </span>
                            <p className="text-xs" data-testid={`ai-analysis-${instanceKey}`}>{analysis.ai_analysis}</p>
                            {analysis.ai_legal_framework && (
                                <div className="text-[10px] opacity-60 font-mono">📜 {analysis.ai_legal_framework}</div>
                            )}
                        </div>
                    </div>
                    <label className={`mt-2 inline-flex items-center gap-1 text-[11px] font-bold cursor-pointer hover:underline ${disabled ? 'opacity-50 pointer-events-none' : ''}`}
                        style={{ color: ACCENT }}>
                        Reemplazar foto
                        <input type="file" accept="image/*" className="hidden" disabled={disabled}
                            data-testid={`photo-input-${instanceKey}`}
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) uploadPhoto(item, instanceKey, f);
                                e.target.value = '';
                            }} />
                    </label>
                </div>
            ) : (
                <label className={`flex flex-col items-center justify-center gap-1.5 border-dashed border-2 py-4 cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 ${disabled ? 'opacity-50 pointer-events-none' : ''}`}
                    style={{ borderColor: 'var(--border)' }}>
                    {uploading ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span className="text-[11px]">Analizando con IA...</span>
                        </>
                    ) : (
                        <>
                            <Camera className="w-4 h-4" style={{ color: AI_ORANGE }} />
                            <span className="text-[11px] font-bold">Subir foto (la IA la revisará)</span>
                        </>
                    )}
                    <input type="file" accept="image/*" className="hidden"
                        data-testid={`photo-input-${instanceKey}`}
                        disabled={uploading || disabled}
                        onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) uploadPhoto(item, instanceKey, f);
                            e.target.value = '';
                        }} />
                </label>
            )}
        </div>
    );
}

function PerInstanceExpander({ parent, count, allItems, responses, onAnswer, onObs, uploadPhoto, uploadingItem, photoAnalyses, disabled = false }) {
    // Buscar items del mismo área con phase='evaluacion' que se van a multiplicar
    const evalItems = allItems.filter((i) =>
        i.id !== parent.id &&
        i.phase === 'evaluacion' &&
        (i.area || '') === (parent.area || '')
    );
    if (evalItems.length === 0 || count <= 0) return null;
    return (
        <div className="mt-3 border-l-2 pl-3 py-2 space-y-3" style={{ borderColor: '#A855F7' }}>
            <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#A855F7' }}>
                ⤷ Se generarán {count} sub-checklist{count > 1 ? 's' : ''} para esta área
            </div>
            {Array.from({ length: count }).map((_, idx) => (
                <div key={idx} className="border p-3" style={{ borderColor: 'var(--border)' }}>
                    <div className="text-xs font-bold mb-2" style={{ color: '#A855F7' }}>
                        {parent.area || 'Instancia'} #{idx + 1}
                    </div>
                    <div className="space-y-2">
                        {evalItems.map((it) => {
                            const key = `${it.id}__${idx}`;
                            const v = responses.answers[key];
                            return (
                                <div key={key} className="border p-2" style={{ borderColor: 'var(--border)' }}>
                                    <div className="flex items-start gap-2 mb-1">
                                        <p className="text-xs flex-1">{it.question}</p>
                                        <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5"
                                            style={{ background: `${WEIGHT_COLOR[it.weight]}22`, color: WEIGHT_COLOR[it.weight] }}>
                                            {WEIGHT_LABEL[it.weight]}
                                        </span>
                                    </div>
                                    <AnswerInput item={it} value={v} onChange={(nv) => onAnswer(key, nv)} disabled={disabled} />
                                    {(it.requires_photo || it.answer_type === 'photo') && (
                                        <PhotoAnalyzer item={it} instanceKey={key}
                                            uploadPhoto={uploadPhoto}
                                            uploading={uploadingItem === key}
                                            analysis={photoAnalyses[key]}
                                            disabled={disabled} />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
}
